<template>
  <b-modal
    id="modal_ciot"
    ref="modal_form"
    :title="modalTitle"
    :ok-title="modalButton"
    close-title="Fechar"
    cancel-title="Cancelar"
    :ok-disabled="isSubmitDisabled"
    v-model="showModal"
    @ok="onStore"
    size="md"
    centered
    no-close-on-backdrop
  >
    <form
      @submit.stop.prevent="onStore"
      @keydown="form.errors.clear($event.target.name)"
    >
      <b-row>
        <b-col>
          <div class="custom-toggle-container mb-2">
            <div
              class="custom-toggle-option"
              :class="{ active: form.modality === 'automatic' }"
              @click="form.modality = 'automatic'"
            >
              <i class="fa fa-bolt"></i> Automático
            </div>
            <div
              class="custom-toggle-option"
              :class="{ 
                active: form.modality === 'manual',
                disabled: !hasManualPermission 
              }"
              @click="hasManualPermission && (form.modality = 'manual')"
              v-b-tooltip.hover
              :title="!hasManualPermission ? 'Você não possui permissão de inserção de CIOT manual' : ''"
            >
              <i class="fa fa-pencil"></i> Manual
            </div>
          </div>
            <div class="invalid-feedback d-block" v-if="form.errors.has('modality')">
              <i class="fa fa-info-circle"></i> {{ form.errors.get('modality') }}
            </div>
        </b-col>
      </b-row>

      <template v-if="form.modality === 'automatic'">
        <b-row>
          <b-col>
            <label class="text-muted mt-1 mb-1" style="font-size: 0.9rem;">Meios de pagamento:</label>
            <div class="mb-2">
              <b-form-radio-group
                v-model="form.tp_tag"
                :options="filteredPaymentOptions"
                name="tp_tag"
                stacked
                value-field="value"
                @change="onPaymentMethodChange"
              ></b-form-radio-group>
            </div>
            <div class="invalid-feedback d-block" v-if="getFieldError('tp_tag')">
              <i class="fa fa-info-circle"></i> {{ getFieldError('tp_tag') }}
            </div>
          </b-col>
        </b-row>
      </template>

      <template v-if="form.modality === 'manual'">
        <b-row>
          <b-col sm="6" class="mb-3">
            <label><span class="text-danger">*</span> Número CIOT</label>
            <div class="validation-input-wrapper">
            <b-form-input
              v-model="form.ciot"
              type="text"
              name="ciot"
              maxlength="12"
              @input="onFieldInput('ciot', $event)"
              @blur="onFieldBlur('ciot')"
              placeholder="000000000000"
              class="pr-5"
              :state="getManualFieldState('ciot')"
            ></b-form-input>
              <span
                v-if="shouldShowFieldIcon('ciot')"
                class="validation-input-icon"
                :class="{
                  valid: getManualFieldState('ciot') === true,
                  invalid: getManualFieldState('ciot') === false
                }"
              >
                <i :class="getManualFieldState('ciot') === true ? 'fa fa-check-circle-o' : 'fa fa-times-circle-o'"></i>
              </span>
            </div>
            <div class="invalid-feedback d-block" v-if="getFieldError('ciot')">
              <i class="fa fa-info-circle"></i> {{ getFieldError('ciot') }}
            </div>
          </b-col>
          <b-col sm="6" class="mb-3">
            <label><span class="text-danger">*</span> Protocolo ANTT</label>
            <div class="validation-input-wrapper">
              <b-form-input
                v-model="form.protocol_antt"
                type="text"
                name="protocol_antt"
                maxlength="4"
                @input="onFieldInput('protocol_antt', $event)"
                @blur="onFieldBlur('protocol_antt')"
                placeholder="0000"
                class="pr-5"
                :state="getManualFieldState('protocol_antt')"
              ></b-form-input>
              <span
                v-if="shouldShowFieldIcon('protocol_antt')"
                class="validation-input-icon"
                :class="{
                  valid: getManualFieldState('protocol_antt') === true,
                  invalid: getManualFieldState('protocol_antt') === false
                }"
              >
                <i :class="getManualFieldState('protocol_antt') === true ? 'fa fa-check-circle-o' : 'fa fa-times-circle-o'"></i>
              </span>
            </div>
            <div class="invalid-feedback d-block" v-if="getFieldError('protocol_antt')">
              <i class="fa fa-info-circle"></i> {{ getFieldError('protocol_antt') }}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="6" class="mb-3">
            <label><span class="text-danger" v-if="!form.route_without_toll">*</span> Vale pedágio</label>
            <div class="validation-input-wrapper">
              <b-form-input
                v-model="form.vpo"
                type="text"
                name="vpo"
                :disabled="form.route_without_toll"
                placeholder="000000000000"
                class="pr-5"
                :state="getManualFieldState('vpo')"
                @input="onFieldInput('vpo', $event)"
                @blur="onFieldBlur('vpo')"
              ></b-form-input>
              <span
                v-if="shouldShowFieldIcon('vpo')"
                class="validation-input-icon"
                :class="{
                  valid: getManualFieldState('vpo') === true,
                  invalid: getManualFieldState('vpo') === false
                }"
              >
                <i :class="getManualFieldState('vpo') === true ? 'fa fa-check-circle-o' : 'fa fa-times-circle-o'"></i>
              </span>
            </div>
            <div class="invalid-feedback d-block" v-if="getFieldError('vpo')">
              <i class="fa fa-info-circle"></i> {{ getFieldError('vpo') }}
            </div>
          </b-col>
          <b-col sm="6" class="mb-3">
            <label><span class="text-danger" v-if="!form.route_without_toll">*</span> Valor do pedágio</label>
            <div class="validation-input-wrapper" v-if="!form.route_without_toll">
              <b-form-input
                v-model="form.toll_value"
                v-money="moneyConfig"
                type="text"
                name="toll_value"
                class="text-left pr-5"
                placeholder="R$ 0,00"
                :state="getManualFieldState('toll_value')"
                @input="onFieldInput('toll_value', $event)"
                @blur="onFieldBlur('toll_value')"
              ></b-form-input>
              <span
                v-if="shouldShowFieldIcon('toll_value')"
                class="validation-input-icon"
                :class="{
                  valid: getManualFieldState('toll_value') === true,
                  invalid: getManualFieldState('toll_value') === false
                }"
              >
                <i :class="getManualFieldState('toll_value') === true ? 'fa fa-check-circle-o' : 'fa fa-times-circle-o'"></i>
              </span>
            </div>
            <b-form-input
              v-else
              :value="''"
              type="text"
              name="toll_value_disabled"
              disabled
              class="text-left"
              placeholder="R$ 0,00"
            ></b-form-input>
            <div class="invalid-feedback d-block" v-if="getFieldError('toll_value')">
              <i class="fa fa-info-circle"></i> {{ getFieldError('toll_value') }}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12" class="mb-3 d-flex align-items-center">
            <b-form-checkbox v-model="form.route_without_toll">
              Esta rota não possui pedágio
            </b-form-checkbox>
          </b-col>
        </b-row>
      </template>
    </form>
    <div slot="modal-footer" class="w-100 d-flex justify-content-between align-items-center">
      <div class="spinner-container d-flex align-items-center">
        <spinner v-if="loading" size="24"></spinner>
        <span v-if="loading" class="ml-2 text-muted" style="font-size: 0.9rem;">Processando...</span>
      </div>
      <div class="custom-footer">
        <b-btn @click="showModal = false" class="btn-voltar"> Voltar </b-btn>
        <b-btn @click="onStore()" :disabled="isSubmitDisabled" class="btn-enviar">
          Enviar
        </b-btn>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { VMoney } from "v-money";

export default {
  name: "ExpedicaoModalCiot",
  directives: {
    money: VMoney,
  },
  props: {
    onSuccessCallback: { type: Function },
  },
  data() {
    return {
      modalTitle: "Emissão de CIOT e pedágio",
      modalButton: "Enviar",
      loading: false,
      isDisabled: false,
      showModal: false,
      hasTollOnRoute: false,
      touchedFields: {
        tp_tag: false,
        ciot: false,
        protocol_antt: false,
        vpo: false,
        toll_value: false,
      },
      forceShowValidation: false,
      emissionModeOptions: [
        { text: "Automático", value: "automatic" },
        { text: "Manual", value: "manual" },
      ],
      paymentMethodOptions: [
        { text: "Tag Sem Parar", value: 2 },
        { text: "Tag Veloe", value: 5 },
        { text: "Tag ConectCar", value: 6 },
        { text: "Tag Move Mais", value: 7 },
        { text: "Tag Taggy", value: 9 },
      ],
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        suffix: "",
        precision: 2,
        masked: false,
        max: 9999.99
      },
      form: new Form({
        shipment_id: "",
        modality: "automatic",
        tp_tag: "",
        ciot: "",
        protocol_antt: "",
        route_without_toll: false,
        vpo: "",
        toll_value: "",
      }),
    };
  },
  computed: {
    filteredPaymentOptions() {
      if (this.hasTollOnRoute) {
        return this.paymentMethodOptions;
      }
      return [{ text: "Rota sem pedágio", value: 4 }];
    },
    hasManualPermission() {
      return this.getPermissionFw('ciot.store');
    },
    manualErrors() {
      if (this.form.modality !== "manual") return {};

      const errors = {};
      const ciotError = this.validateCiot();
      const protocolError = this.validateProtocolAntt();

      if (ciotError) errors.ciot = ciotError;
      if (protocolError) errors.protocol_antt = protocolError;

      if (!this.form.route_without_toll) {
        const vpoError = this.validateVpo();
        const tollValueError = this.validateTollValue();
        if (vpoError) errors.vpo = vpoError;
        if (tollValueError) errors.toll_value = tollValueError;
      }

      return errors;
    },
    automaticErrors() {
      if (this.form.modality !== "automatic") return {};
      if (!this.hasTollOnRoute || this.form.tp_tag) return {};
      return { tp_tag: "Selecione um meio de pagamento." };
    },
    isSubmitDisabled() {
      if (this.loading || this.isDisabled) return true;

      if (this.form.modality === "automatic") {
        return !!this.automaticErrors.tp_tag;
      }

      return Object.keys(this.manualErrors).length > 0;
    },
  },
  watch: {
    "form.route_without_toll"(newVal) {
      if (newVal) {
        this.form.vpo = "";
        this.form.toll_value = "";
        this.touchedFields.vpo = false;
        this.touchedFields.toll_value = false;

        if (this.form.errors.has('vpo')) {
          this.form.errors.clear('vpo');
        }
        if (this.form.errors.has('toll_value')) {
          this.form.errors.clear('toll_value');
        }
      }
    },
    "form.modality"(newVal) {
      this.form.errors.clear();
      this.forceShowValidation = false;
      this.resetTouchedFields();
      if (newVal === "automatic") {
        if (!this.hasTollOnRoute) {
          this.form.tp_tag = 4;
        } else {
          this.form.tp_tag = "";
        }
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  },
  methods: {
    getManualFieldState(fieldName) {
      if (this.form.modality !== "manual") return null;
      if (this.form.route_without_toll && (fieldName === "vpo" || fieldName === "toll_value")) return null;
      if (!this.shouldShowFieldError(fieldName) && !this.hasFieldValue(fieldName)) return null;
      if (!this.hasFieldValue(fieldName)) return null;

      const fieldValidator = {
        ciot: this.validateCiot,
        protocol_antt: this.validateProtocolAntt,
        vpo: this.validateVpo,
        toll_value: this.validateTollValue,
      }[fieldName];

      if (!fieldValidator) return null;
      return fieldValidator.call(this) ? false : true;

    },
    shouldShowFieldIcon(fieldName) {
      const fieldState = this.getManualFieldState(fieldName);
      return fieldState !== null;
    },
    hasFieldValue(fieldName) {
      const fieldValue = this.form[fieldName];
      if (fieldValue === null || fieldValue === undefined) return false;
      if (fieldName === "toll_value") {
        const normalizedValue = String(fieldValue)
          .replace(/[^\d,-]/g, "")
          .replace(",", ".");
        const numericValue = Number.parseFloat(normalizedValue);
        return !Number.isNaN(numericValue) && numericValue > 0;
      }
      if (typeof fieldValue === "number") return fieldValue > 0;
      return String(fieldValue).trim().length > 0;
    },
    handleClickOutside(event) {
      if (this.showModal && event.target.classList.contains("modal")) {
        if (this.form.modality === "manual") {
          this.forceValidationFeedback();
        } else if (this.hasTollOnRoute && !this.form.tp_tag) {
          this.forceValidationFeedback();
        }
      }
    },
    resetTouchedFields() {
      this.touchedFields = {
        tp_tag: false,
        ciot: false,
        protocol_antt: false,
        vpo: false,
        toll_value: false,
      };
    },
    shouldShowFieldError(fieldName) {
      return this.forceShowValidation || this.touchedFields[fieldName];
    },
    getFieldError(fieldName) {
      if (!this.shouldShowFieldError(fieldName)) return null;

      if (fieldName === "tp_tag") {
        return this.automaticErrors.tp_tag || null;
      }

      return this.manualErrors[fieldName] || null;
    },
    onFieldInput(fieldName, value) {
      if (fieldName === "toll_value") {
        const normalizedValue = String(value || "")
          .replace(/[^\d,-]/g, "")
          .replace(",", ".");
        const numericValue = Number.parseFloat(normalizedValue);

        if (
          !this.touchedFields.toll_value &&
          (normalizedValue === "" || Number.isNaN(numericValue) || numericValue <= 0)
        ) {
          this.form[fieldName] = value;
          return;
        }
      }

      this.touchedFields[fieldName] = true;

      if (fieldName === "ciot" || fieldName === "protocol_antt") {
        this.form[fieldName] = (value || "").toUpperCase();
        return;
      }

      this.form[fieldName] = value;
    },
    onFieldBlur(fieldName) {
      if ((fieldName === "vpo" || fieldName === "toll_value") && this.form.route_without_toll) {
        return;
      }
      this.touchedFields[fieldName] = true;
    },
    onPaymentMethodChange() {
      this.touchedFields.tp_tag = true;
    },
    forceValidationFeedback() {
      this.forceShowValidation = true;
      Object.keys(this.touchedFields).forEach((key) => {
        if (key === "vpo" || key === "toll_value") {
          this.touchedFields[key] = !this.form.route_without_toll;
          return;
        }
        this.touchedFields[key] = true;
      });
    },
    onCreate(data) {
      this.isDisabled = false;
      this.loading = false;
      this.form.reset();
      this.form.errors.clear();
      this.forceShowValidation = false;
      this.resetTouchedFields();
      this.form.shipment_id = data.cargaId;
      this.hasTollOnRoute = data.cargaRota && data.cargaRota.cargaRotaPedagio && data.cargaRota.cargaRotaPedagio.length > 0;
      this.form.route_without_toll = !this.hasTollOnRoute;
      
      this.form.modality = "automatic";
      if (!this.hasTollOnRoute) {
        this.form.tp_tag = 4;
      } else {
        this.form.tp_tag = "";
      }

      this.$refs.modal_form.show();
    },
    onHide(bvModalEvent) {
      if (bvModalEvent.trigger === 'backdrop') {
        bvModalEvent.preventDefault();
        this.forceValidationFeedback();
      }
    },
    validateCiot() {
      if (!this.form.ciot) return "Campo obrigatório";
      if (/[^a-zA-Z0-9]/.test(this.form.ciot)) return "Somente letras e números são permitidos";
      if (this.form.ciot.length < 12) return `CIOT incompleto. ${this.form.ciot.length}/12 caracteres`;
      if (this.form.ciot.length > 12) return "O CIOT deve conter no máximo 12 caracteres.";
      return null;
    },
    validateProtocolAntt() {
      if (!this.form.protocol_antt) return "Campo obrigatório";
      if (/[^a-zA-Z0-9]/.test(this.form.protocol_antt)) return "Somente letras e números são permitidos";
      if (this.form.protocol_antt.length < 4) return `Protocolo incompleto. ${this.form.protocol_antt.length}/4 caracteres`;
      return null;
    },
    validateVpo() {
      if (!this.form.vpo) return "Campo obrigatório";
      if (/[^0-9]/.test(this.form.vpo)) return "Somente números são permitidos";
      return null;
    },
    validateTollValue() {
      let numericValue = 0;
      if (typeof this.form.toll_value === 'string') {
        const cleanString = this.form.toll_value.replace(/[^\d,-]/g, '').replace(',', '.');
        numericValue = parseFloat(cleanString);
      } else {
        numericValue = parseFloat(this.form.toll_value);
      }

      if (isNaN(numericValue)) return "Campo obrigatório";
      if (numericValue <= 0) return "O valor deve ser maior que 0.";
      if (numericValue > 9999.99) return "O valor máximo permitido é de R$ 9.999,99.";
      return null;
    },
    validateManualForm() {
      if (this.form.route_without_toll) {
        this.form.vpo = "";
        this.form.toll_value = "";
      }
      return Object.keys(this.manualErrors).length === 0;
    },
    onStore() {
      this.forceValidationFeedback();

      if (this.form.modality === "manual") {
        if (!this.validateManualForm()) {
          return;
        }
      } else {
        if (this.hasTollOnRoute && !this.form.tp_tag) {
          return;
        }
      }

      this.loading = true;
      this.isDisabled = true;

      const endpoint = this.form.modality === "automatic" 
        ? "/api/expedicao/geraciot" 
        : "/api/expedicao/geraciotmanual";

      const payload = {};

      if (this.form.modality === "automatic") {
        payload.carga_id = this.form.shipment_id;
        payload.tp_tag = this.form.tp_tag;
      } else {
        payload.shipment_id = this.form.shipment_id;
        payload.ciot = this.form.ciot;
        payload.protocol_antt = this.form.protocol_antt;
        payload.route_without_toll = this.form.route_without_toll;
        payload.vpo = this.form.vpo;
        payload.toll_value = this.form.toll_value;
      }

      const submitForm = new Form(payload);

      submitForm
        .post(endpoint)
        .then((response) => {
          new ReturnApi(response, this, true, null, null, true);
          if (this.onSuccessCallback) {
            this.onSuccessCallback();
          }
          this.showModal = false;
        })
        .catch((errors) => {
          new ReturnApi(errors, this, true, null, null, false);
        });
    },
  },
};
</script>

<style scoped>
.custom-toggle-container {
  display: flex;
  background-color: #f1f3f5;
  border-radius: 6px;
  width: 100%;
  max-width: 220px;
}
.custom-toggle-option {
  flex: 1;
  text-align: center;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  user-select: none;
}
.custom-toggle-option i {
  margin-right: 4px;
}
.custom-toggle-option.active {
  background-color: #ffffff;
  color: #212529;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.custom-toggle-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.custom-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}
.btn-voltar {
  background-color: #ffffff;
  color: #212529;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 6px 16px;
  font-weight: 500;
}
.btn-voltar:hover {
  background-color: #f8f9fa;
  color: #212529;
  border-color: #ced4da;
}
.btn-enviar {
  background-color: #f1f3f5;
  color: #adb5bd;
  border: 1px solid #f1f3f5;
  border-radius: 6px;
  padding: 6px 16px;
  font-weight: 500;
}
.btn-enviar:not(:disabled) {
  background-color: #34527a;
  color: #ffffff;
  border-color: #34527a;
}
.btn-enviar:not(:disabled):hover {
  background-color: #2c4566;
  border-color: #2c4566;
}

/* Estilização customizada para os radio buttons */
::v-deep .custom-radio .custom-control-label::before {
  border-radius: 50%;
  border: 1px solid #ced4da;
  background-color: #fff;
}
::v-deep .custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}
::v-deep .custom-control {
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}
::v-deep .custom-control:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
::v-deep .custom-control-label {
  padding-top: 2px;
  color: #495057;
}

/* Ajustes estruturais do modal */
::v-deep .modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
::v-deep .modal-header {
  border-bottom: 1px solid #e9ecef !important;
  padding: 16px 24px !important;
}
::v-deep .modal-header .modal-title {
  font-weight: 600;
  font-size: 1.15rem;
  color: #212529;
}
::v-deep .modal-body {
  padding: 20px 24px !important;
}
::v-deep .modal-footer {
  border-top: 1px solid #e9ecef !important;
  padding: 16px 24px !important;
}
::v-deep .form-control {
  border-radius: 6px;
}

/* Evita duplicidade com o ícone custom de validação */
::v-deep .form-control.is-valid,
::v-deep .form-control.is-invalid {
  background-image: none !important;
  padding-right: 2.75rem !important;
}

.validation-input-wrapper {
  position: relative;
}

.validation-input-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  line-height: 1;
  pointer-events: none;
  font-size: 1.2rem;
}

.validation-input-icon.valid {
  color: #28a745;
}

.validation-input-icon.invalid {
  color: #dc3545;
}
</style>
